import React from 'react';
import { useProducts } from '../../common/hooks/useProducts';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { pricesAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Card from '../../common/components/Card';
import Table from '../../common/components/Table';
import Loading from '../../common/components/Loading';
import { formatCurrency } from '../../common/utils/formatters';
import toast from 'react-hot-toast';
import './StockScreen.css';

const StockScreen = () => {
  const queryClient = useQueryClient();
  const { isLoading: productsLoading } = useProducts();

  const { data: prices, isLoading: pricesLoading } = useQuery('prices', async () => {
    const response = await pricesAPI.list();
    return response.data.data;
  });

  const updateStockMutation = useMutation(
    ({ id, stockQuantity }) => pricesAPI.updateStock(id, stockQuantity),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('prices');
        toast.success('Stock updated successfully!');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update stock');
      },
    }
  );

  const handleStockUpdate = (priceId, currentStock) => {
    const newStock = prompt('Enter new stock quantity:', currentStock);
    if (newStock !== null && !isNaN(newStock)) {
      updateStockMutation.mutate({ id: priceId, stockQuantity: parseInt(newStock) });
    }
  };

  if (productsLoading || pricesLoading) {
    return <Loading message="Loading stock information..." />;
  }

  const productPrices = prices || [];

  const columns = [
    { header: 'Product', key: 'product_name' },
    { header: 'Size', key: 'size_option_name' },
    {
      header: 'Price',
      render: (row) => formatCurrency(row.price),
      align: 'right',
    },
    {
      header: 'Stock',
      key: 'stock_quantity',
      align: 'right',
    },
    {
      header: 'Available',
      render: (row) => (
        <span className={`status-badge ${row.is_available ? 'active' : 'inactive'}`}>
          {row.is_available ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outline"
          onClick={() => handleStockUpdate(row.id, row.stock_quantity)}
        >
          Update Stock
        </Button>
      ),
    },
  ];

  return (
    <div className="stock-screen">
      <div className="screen-header">
        <div>
          <h1>Farm First Producer</h1>
          <p>Manage inventory levels for your products</p>
        </div>
      </div>

      <Card>
        <Table columns={columns} data={productPrices} emptyMessage="No products with pricing yet. Add products and set prices first!" />
      </Card>
    </div>
  );
};

export default StockScreen;
